/* Breakz — making drawer search visible to analytics.
 *
 * Why this is needed at all. Measured on the storefront, not deduced:
 *
 *   /search?q=charizard  → pixels receive search_submitted. Shopify
 *                          sends it itself, nothing to do.
 *   typing in the drawer  → pixels receive NOTHING.
 *
 * The drawer calls /search/suggest in the background, the form is never
 * submitted, the page never reloads — a standard event has nowhere to
 * come from. And most searching here happens in the drawer: it opens
 * from a header button on every page. Without this file GA4 would show
 * the smaller part of search, silently and plausibly — the worst kind
 * of leaky analytics.
 *
 * The event name must be prefixed. A storefront may publish only its own
 * events: "partners and merchants cannot publish standard events", and
 * the docs require a prefix so as not to clash with standard names.
 * Hence breakz:search, not search_submitted.
 *
 * Tracking consent is deliberately not checked here. Publishing only
 * hands data to the pixel manager; whether it goes further is decided by
 * the pixel itself through its own consent setting. Duplicating that
 * check here would create a second place for it to drift out of sync.
 *
 * No personal data is sent: only the query string and the number of
 * results. What someone typed into search is the thing being measured.
 */
(function () {
  /* How long to wait before treating a query as real.
   *
   * The results themselves refresh 200 ms after a keystroke — otherwise
   * the drawer would feel sluggish. Sending the event at the same rate is
   * not an option: typing "charizard" would produce nine "searches" —
   * "c", "ch", "cha". In a report that looks like real demand for "c".
   *
   * We wait for 1.2 s of silence. Every further keystroke cancels the
   * previous countdown, so only what the person settled on goes up. */
  var SETTLE = 1200;

  var timer = 0;
  var pending = null;
  var last = '';

  function publish(item) {
    if (!item || !item.query) return;
    // We do not send the same query twice in a row: someone may have gone
    // back into the field and reopened it; that is not extra searching.
    if (item.query === last) return;

    var a = window.Shopify && window.Shopify.analytics;
    if (!a || typeof a.publish !== 'function') return;

    last = item.query;
    a.publish('breakz:search', {
      query: item.query,
      // null if the results had not answered yet or the request was cancelled.
      // Zero and "unknown" are different things and must not be confused in a report.
      results: item.results,
      no_results: item.results === 0,
      source: 'suggest',
    });
  }

  function schedule(item) {
    clearTimeout(timer);
    pending = item;
    timer = setTimeout(function () {
      publish(item);
      pending = null;
    }, SETTLE);
  }

  /* Send immediately. Needed when someone leaves through a result:
   * the page changes before the countdown ends and the timer dies with
   * it. A click on a card is the strongest confirmation that the search
   * happened; losing those events would be the worst loss. */
  function flush() {
    if (!pending) return;
    clearTimeout(timer);
    publish(pending);
    pending = null;
  }

  function cancel() {
    clearTimeout(timer);
    pending = null;
  }

  function attach(component) {
    if (component.dataset.bxaOn) return;
    component.dataset.bxaOn = '1';

    /* We listen on the component itself, not on document. The same event
     * is dispatched by facets on the results page, and Shopify counts
     * that page itself — catching them here would double every filtered search. */
    component.addEventListener('shopify:search:update', function (event) {
      var query = event.search && event.search.query ? String(event.search.query).trim() : '';
      if (!query) return;

      var item = { query: query, results: null };
      schedule(item);

      if (event.promise && typeof event.promise.then === 'function') {
        event.promise
          .then(function (result) {
            // While the countdown runs we append the count to the same record.
            // If the query has already changed, this record is no longer current.
            if (pending === item && result && typeof result.totalCount === 'number') {
              item.results = result.totalCount;
            }
          })
          .catch(function () {
            /* A cancelled request is normal: the person kept typing.
               We stay quiet, otherwise the console fills up on every word. */
          });
      }
    });

    component.addEventListener('click', function (event) {
      if (event.target.closest && event.target.closest('a[href]')) flush();
    });

    /* A submit goes to /search, and there Shopify sends search_submitted
     * itself. Our own event at that moment would be exactly the same
     * thing a second time. We cancel the countdown. */
    component.addEventListener('submit', cancel);
  }

  function boot() {
    document.querySelectorAll('predictive-search-component').forEach(attach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
